import React, { useMemo } from 'react';

import { useMediaElement } from '../../hooks/use-media-element';
import type { MediaFieldValue } from '../../hooks/use-media-field-adapter';
import type { MediaElement } from '../../hooks/use-media-element';
import { useUniversalMediaUploader } from '../../components/providers/universal-media-uploader-provider';

import MediaField, { LegacyMediaField } from './media-field';
import LabeledField from './labeled-field';
import useMediaUploaderSync from './hooks/use-media-uploader-sync';

interface MediaElementFieldProps {
  elementKey: string;
  element: MediaElement;
  value: MediaFieldValue;
  editing: boolean;
  disabled: boolean;
  visible: boolean;
  required: boolean;
  feature: object;
  record: object;
  description?: string;
  dataTestID?: string;
  onFieldChange: (value: MediaFieldValue) => void;
  onMediaAdd?: (
    value: MediaFieldValue,
    metadata: Record<string, unknown>,
    callback: () => void,
  ) => void;
  onMediaRemove?: (
    value: MediaFieldValue,
    info: { id: string },
  ) => void;
}

/**
 * Thin wrapper that bridges the Element-coupled legacy editor
 * (element-list-view) to the generic MediaField component.
 *
 * Calls useMediaElement to resolve type-specific config and construct
 * all callbacks, then renders LabeledField + MediaField.
 *
 * This wrapper exists because element-list-view is a class component
 * that cannot call hooks directly. Once element-list-view is migrated
 * to a function component, the hook can be called inline and this
 * wrapper becomes unnecessary.
 */
const MediaElementField: React.FC<MediaElementFieldProps> = ({
  elementKey,
  element,
  value,
  editing,
  disabled,
  visible,
  required,
  feature,
  record,
  description,
  dataTestID,
  onFieldChange,
  onMediaAdd,
  onMediaRemove,
}) => {
  const mediaFieldProps = useMediaElement({
    element,
    value,
    editing,
    disabled,
    feature,
    record,
    onFieldChange,
    onMediaAdd,
    onMediaRemove,
  });

  const uploaderId = `media-${element.key}`;

  // Always call the hook unconditionally (React rules of hooks), but pass
  // undefined config when the flag is off so the actor stays uninitialized
  // and no Uppy instance is created — avoiding overhead during rollout.
  const uploaderConfig = mediaFieldProps.uploader.isMultiFileEnabled
    ? {
      restrictions: {
        allowedFileTypes: mediaFieldProps.uploader.allowedFileTypes,
        maxNumberOfFiles: mediaFieldProps.uploader.remainingSlots,
        maxFileSize: mediaFieldProps.uploader.maxFileSize,
      },
      onUploadParams: mediaFieldProps.uploader.onUploadParams,
      multiFile: true,
      allowDuplicates: true,
      persist: false,
    } as const
    : undefined;

  const uploader = useUniversalMediaUploader(uploaderId, uploaderConfig);

  useMediaUploaderSync({
    editing,
    uppyInstance: uploader.uppyInstance,
    sendEvent: uploader.sendEvent,
    files: uploader.files,
    isMultiFileEnabled: mediaFieldProps.uploader.isMultiFileEnabled,
    remainingSlots: mediaFieldProps.uploader.remainingSlots,
    allowedFileTypes: mediaFieldProps.uploader.allowedFileTypes,
    maxFileSize: mediaFieldProps.uploader.maxFileSize,
    mediaType: mediaFieldProps.mediaType,
    onAdd: mediaFieldProps.onAdd,
  });

  // For fields with maxItems (e.g. signature), hide the dropzone when
  // in-flight uploads have consumed the remaining slots — even before
  // the upload completes and commits to the field value.
  const shouldShowUploader = useMemo(() => {
    if (!mediaFieldProps.uploader.shouldShow) return false;

    const { remainingSlots } = mediaFieldProps.uploader;
    const committedIds = new Set(mediaFieldProps.items.map((i) => i.mediaID));
    let uncommittedPipelineCount = 0;

    uploader.files.forEach((entry) => {
      if (entry.error) return;
      const attachmentId = entry.attachmentData?.id;
      if (!attachmentId || !committedIds.has(attachmentId)) {
        uncommittedPipelineCount++;
      }
    });

    return remainingSlots > uncommittedPipelineCount;
  }, [
    mediaFieldProps.uploader.shouldShow,
    mediaFieldProps.uploader.remainingSlots,
    mediaFieldProps.items,
    uploader.files,
  ]);

  // Build props conditionally based on feature flag
  const mediaFieldContent = mediaFieldProps.uploader.isMultiFileEnabled ? (
    <MediaField
      items={mediaFieldProps.items}
      renderItem={mediaFieldProps.renderItem}
      modalProps={mediaFieldProps.modalProps}
      editing={mediaFieldProps.editing}
      disabled={mediaFieldProps.disabled}
      uploaderProps={{
        uppy: uploader.uppyInstance,
        mediaType: mediaFieldProps.mediaType!,
        allowedFileTypes: mediaFieldProps.uploader.allowedFileTypes,
        shouldShowUploader,
        files: uploader.files,
        sendEvent: uploader.sendEvent,
        wasRecentlyUploaded: uploader.wasRecentlyUploaded,
        suppressFileRemoved: uploader.suppressFileRemoved,
        unsuppressFileRemoved: uploader.unsuppressFileRemoved,
      }}
      onCompletedStatusClick={mediaFieldProps.onCompletedStatusClick}
      trackEnabled={mediaFieldProps.trackEnabled}
      uploadTrack={mediaFieldProps.uploadTrack}
    />
  ) : (
    <LegacyMediaField
      items={mediaFieldProps.items}
      onRemove={mediaFieldProps.onRemove}
      renderItem={mediaFieldProps.renderItem}
      modalProps={mediaFieldProps.modalProps}
      editing={mediaFieldProps.editing}
      disabled={mediaFieldProps.disabled}
      trackEnabled={mediaFieldProps.trackEnabled}
      uploadTrack={mediaFieldProps.uploadTrack}
      fileTypes={mediaFieldProps.fileTypes}
      buttonText={mediaFieldProps.buttonText}
      uploadFile={mediaFieldProps.uploadFile}
      onAdd={mediaFieldProps.onAdd}
      maxItems={mediaFieldProps.maxItems}
    />
  );

  return (
    <LabeledField
      key={elementKey}
      visible={visible}
      required={required}
      disabled={disabled}
      label={element.label}
      description={description}
      dataTestID={dataTestID}
      bottomBorder={element.elements == null}
      labelAlignTop
    >
      {mediaFieldContent}
    </LabeledField>
  );
};

export default MediaElementField;
